import { useContext, forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';
import { NavHashLink } from 'react-router-hash-link';
import { Manager, Popper, Reference } from 'react-popper';
import useEventOutside from '@omtanke/react-use-event-outside';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/icon/Icon';
import ThemeContext from '../../contexts/themeContext';
import Collapse from '../../components/bootstrap/Collapse';
import useDarkMode from '../../hooks/useDarkMode';
import Badge from '../../components/bootstrap/Badge';
import { eapproval, dashboardMenu } from '../../menu';
import { useMst } from '../../models';

export const List = forwardRef(
	({ id = null,
		children = null,
		className = null,
		ariaLabelledby = null,
		parentId = null,
		rootId = null,
		horizontal = false,
		...props
	}, ref) => {
		return (
			<ul
				ref={ref}
				id={id}
				className={classNames('navigation', { 'navigation-menu': horizontal }, className)}
				aria-labelledby={ariaLabelledby}
				data-bs-parent={
					parentId === `${rootId}__${rootId}`
						? `#${rootId}`
						: (parentId && `#${parentId}`) || null
				}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}>
				{children}
			</ul>
		);
	},
);
List.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	ariaLabelledby: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string,
	horizontal: PropTypes.bool,
};

export const Item = ({
	children = null,
	to = null,
	title = null,
	icon = null,
	id = null,
	parentId = null,
	rootId,
	isHorizontal = false,
	notification = false,
	isMore = false,
	hide = false,
	idText,
	...props
}) => {
	const { darkModeStatus } = useDarkMode();
	const { width } = useWindowSize();
	const { setAsideStatus, setAsideSubStatus, setLeftMenuStatus, setRightMenuStatus,
		noticeCount,
		eApproval,
		mobileDesign,
	} = useContext(ThemeContext);
	const { t } = useTranslation();
	const { navtabmenus } = useMst();
 
	// eslint-disable-next-line react/prop-types
	const _active = props.activeItem === id;

	const handleClick = () => {
		// eslint-disable-next-line react/prop-types,no-unused-expressions
		_active ? props.setActiveItem(null) : props.setActiveItem(id);
		// For Mobile Design: close aside when clicking menu item
		// if (mobileDesign) {
		// 	setAsideStatus(false);
		// 	setAsideSubStatus(false);
		// }
	};

	const linkHandleClick = () => {
		// For Mobile Design
		if (mobileDesign) setAsideStatus(false);
		if (mobileDesign) setAsideSubStatus(false);
		
		setLeftMenuStatus(false);
		setRightMenuStatus(false);

		if(navtabmenus.get.list.findIndex( item => item.path === to) === -1) {
			let lists = (navtabmenus.get?.list || [])?.map( v => {
				return {...v, isActive: to === '/'+v.path ? true: false}
			})
			lists.push({
				id: id,
				name: title,
				path: to,
				isActive: true,
				idText: idText
			})
			navtabmenus.setMenus(lists);
		} else {
			let lists = navtabmenus.get.list?.map( v => {
				return {...v, isActive: to === '/'+v.path ? true: false}
			})
			navtabmenus.setMenus(lists);
		}
	};

	const _anchorLinkPattern = /^#/i;
	const location = useLocation();

	// For aside menu
	// [2025.07.23]하위 메뉴 선택시 상단 메뉴도 active를 설정하려고 했으나 pathname이 제대로 설정되어있지 않아 안쓰이는중
	const here = to !== '/' && location.pathname.includes(to);
	// For top menu
	const match = to !== '/' && location.pathname === to;

	const _LinkClass = classNames('navigation-link', 'navigation-link-pill', {
		collapsed: !!children && !isHorizontal,
		active: isHorizontal ? match : match,
	});

	/* color-blue-light */
	const _Inner = (
		<>
			<span className='navigation-link-info'>
				{icon && <Icon className='navigation-icon' icon={icon} />}
				<span className='navigation-text'>{t(title)}</span>
				{to === '/board/notice' && noticeCount > 0 && (
					<Badge color='danger' className='ms-1'>{noticeCount > 99 ? '99+' : noticeCount}</Badge>
				)}
			</span>
			{(!!children || !!notification) && (
				<span className='navigation-link-extra'>
					{!!notification && (
						<Icon
							icon='Circle'
							className={classNames(
								'navigation-notification me-1',
								{
									[`text-${notification}`]: typeof notification === 'string',
									'text-danger': typeof notification !== 'string',
								},
								'animate__animated animate__heartBeat animate__infinite animate__slower',
							)}
						/>
					)}
					{!!children && <Icon className='navigation-arrow' icon='ChevronDown' size='lg' />}
				</span>
			)}
		</>
	);
	
	const _withoutChild =
		!children &&
		!hide &&
		((_anchorLinkPattern.test(to) && (
			<NavHashLink className={_LinkClass} to={to} onClick={linkHandleClick}>
				{_Inner}
			</NavHashLink>
		)) || (
			<NavLink
				className={classNames(_LinkClass, ({ isActive }) => (isActive ? 'active' : ''))}
				to={`../${to}`}
				onClick={linkHandleClick}>
				{_Inner}
				<>
				{/* 게시판 */}
				{null}
				{/* 전자결재 */}
				{Object.values(eapproval)?.map( v => to === v.path && v.id in eApproval && eApproval[v.id] > 0 ? <span key={`eapprovalBadge-${v.id}`} className='small text-white'>{eApproval[v.id] > 999 ? '999+' : eApproval[v.id]}</span> : null )}
				</>
			</NavLink>
		));

	// Dropdown
	const dropdownRef = useRef(null);

	const dropdownButtonRef = useRef(null);
	const setButtonRef = useCallback((node, ref) => {
		dropdownButtonRef.current = node;
		return ref(node);
	}, []);

	const dropdownListRef = useRef(null);
	const setListRef = useCallback((node, ref) => {
		dropdownListRef.current = node;
		return ref(node);
	}, []);

	const [dropdownStatus, setDropdownStatus] = useState(false);

	const dropdownButtonHandleClick = () => {
		setDropdownStatus(!dropdownStatus);
	};

	// Clicking outside to close
	const closeMenu = useCallback(() => {
		setDropdownStatus(false);
	}, []);
	useEventOutside(dropdownRef, 'mousedown', closeMenu);
	useEventOutside(dropdownRef, 'touchstart', closeMenu);

	if (children) {
		// submenu && in header
		if (isHorizontal) {
			return (
				<Manager>
					<li
						ref={dropdownRef}
						className={classNames('navigation-item', 'dropdown', {
							'navigation-item-more': isMore,
						})}>
						<Reference>
							{({ ref }) => (
								<span
									ref={(node) => setButtonRef(node, ref)}
									id={`${rootId}__${id}--link`}
									className={_LinkClass}
									// data-bs-toggle='dropdown'
									// data-bs-target={`#${rootId}__${id}`}
									aria-expanded={dropdownStatus}
									aria-controls={`${rootId}__${id}`}
									role='button'
									tabIndex='-1'
									onClick={dropdownButtonHandleClick}
									onKeyDown={dropdownButtonHandleClick}>
									{_Inner}
								</span>
							)}
						</Reference>
						{dropdownStatus && (
							<Popper
								placement='bottom-start'
								modifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: [`bottom-end`, `bottom-start`],
										},
									},
								]}>
								{({ ref, style, placement }) => (
									<List
										ref={(node) => setListRef(node, ref)}
										style={style}
										data-placement={placement}
										id={`${rootId}__${id}`}
										className={classNames(
											'dropdown-menu',
											{
												'dropdown-menu-dark': darkModeStatus,
											},
											'show',
										)}
										ariaLabelledby={`${rootId}__${id}--link`}
										rootId={rootId}
										parentId={`${rootId}__${parentId}`}
										onMouseLeave={() => setDropdownStatus(false)}>
										{children}
									</List>
								)}
							</Popper>
						)}
					</li>
				</Manager>
			);
		}
		// submenu && in aside
		return (
			<li className='navigation-item'>
				<span
					id={`${rootId}__${id}--link`}
					className={_LinkClass}
					// data-bs-toggle='collapse'
					// data-bs-target={`#${rootId}__${id}`}
					aria-expanded={_active}
					aria-controls={`${rootId}__${id}`}
					role='button'
					tabIndex='-1'
					onClick={handleClick}
					onKeyDown={handleClick}>
					{_Inner}
				</span>
				<Collapse isOpen={_active} isChildClone>
					<List
						id={`${rootId}__${id}`}
						ariaLabelledby={`${rootId}__${id}--link`}
						rootId={rootId}
						parentId={`${rootId}__${parentId}`}
						onMouseLeave={closeMenu}>
						{children}
					</List>
				</Collapse>
			</li>
		);
	}
	// without submenu
	if (hide) return null;
	return <li className='navigation-item'>{_withoutChild}</li>;
};
Item.propTypes = {
	children: PropTypes.node,
	to: PropTypes.string,
	title: PropTypes.string,
	icon: PropTypes.string,
	id: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string.isRequired,
	isHorizontal: PropTypes.bool,
	notification: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	isMore: PropTypes.bool,
	hide: PropTypes.bool,
};

export const NavigationLine = ({ className = null }) => {
	return <hr className={classNames('navigation-line', className)} />;
};
NavigationLine.propTypes = {
	className: PropTypes.string,
};

export const NavigationTitle = ({ className, children, ...props }) => {
 
	return (
		<li className='navigation-item'>
			{/* eslint-disable-next-line react/jsx-props-no-spreading */}
			<span className={classNames('navigation-title', className)} {...props}>
				{children}
			</span>
		</li>
	);
};
NavigationTitle.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
NavigationTitle.defaultProps = {
	className: null,
};

const Navigation = forwardRef(({ menu, horizontal = false, id, className = null, ...props }, ref) => {
	const [activeItem, setActiveItem] = useState(null);

	// URL이 바뀔 때마다 현재 경로에 맞는 그룹을 자동으로 펼치는 코드
	// (+ 대시보드로 최초 진입 시에는, URL 매칭 대신 메뉴에서 가장 먼저 나오는 그룹을 대신 펼쳐줌)
	const lastProcessedPath = useRef(null);
	const location = useLocation();

	useEffect(() => {
		const currentPath = location.pathname.replace(/^\//, '');
		if (currentPath === '') return;

		// 이미 처리한 적 있는 경로라면(StrictMode 이중 실행 등으로 같은 경로에 대해 또 실행되는 경우) 아무것도 하지 않고 건너뜀
		if (lastProcessedPath.current === currentPath) return;

		const isFirstEverPath = lastProcessedPath.current === null;
		lastProcessedPath.current = currentPath;

		const findParentIdByPath = (data) => {
			for (const key of Object.keys(data)) {
				const item = data[key];
				if (item.subMenu) {
					const isChildActive = Object.values(item.subMenu).some(
						(child) => child.path && child.path.replace(/^\//, '') === currentPath
					);
					if (isChildActive) return item.id;
				}
			}
			return null;
		};

		const isDashboardPath = currentPath === dashboardMenu.dashboard.path.replace(/^\//, '');

		let parentId = null;

		if (isDashboardPath && isFirstEverPath) {
			const firstGroupKey = Object.keys(menu).find((key) => menu[key].subMenu);
			if (firstGroupKey) {
				parentId = menu[firstGroupKey].id;
			}
		}

		if (!parentId) {
			parentId = findParentIdByPath(menu);
		}

		if (parentId) setActiveItem(parentId);
	}, [location.pathname, menu]);

	const { t } = useTranslation();

	function fillMenu(data, parentId, rootId, isHorizontal, isMore) {

		return Object.keys(data).map((item) =>
			data[item].path ? (
				<Item
					key={data[item].id}
					rootId={rootId}
					id={data[item].id}
					title={data[item].text}
					idText={data[item]?.idText}
					icon={data[item].icon}
					to={`${data[item].path}`}
					parentId={parentId}
					isHorizontal={isHorizontal}
					setActiveItem={setActiveItem}
					activeItem={activeItem}
					notification={data[item].notification}
					hide={data[item].hide}>
					{!!data[item].subMenu &&
						fillMenu(data[item].subMenu, data[item].id, rootId, isHorizontal)}
				</Item>
			) : (
				!isMore &&
				!isHorizontal && (
					<NavigationTitle key={data[item].id}>{t(data[item].text)}</NavigationTitle>
				)
			),
		);
	}

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<nav ref={ref} aria-label={id} className={className} {...props}>
			<List id={id} horizontal={horizontal}>
				{fillMenu(menu, id, id, horizontal)}
				{horizontal && (
					<Item
						rootId={`other-${id}`}
						title={t('More')}
						icon='MoreHoriz'
						isHorizontal
						isMore>
						{fillMenu(menu, `other-${id}`, `other-${id}`, false, true)}
					</Item>
				)}
			</List>
		</nav>
	);
});
Navigation.propTypes = {
	horizontal: PropTypes.bool,
	menu: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		text: PropTypes.string,
		idText: PropTypes.string,
		path: PropTypes.string,
		icon: PropTypes.string,
		isDisable: PropTypes.bool,
		subMenu: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				text: PropTypes.string,
				path: PropTypes.string,
				icon: PropTypes.string,
				isDisable: PropTypes.bool,
			}),
		),
	}).isRequired,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	className: PropTypes.string,
};

export default Navigation;
